import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { GenericCocktailMatch, VenueMatchResult, VenuePreferences } from "@vibetail/contracts";
import { useLang } from "../../../lib/i18n.js";
import { FLAVOR_CHIPS } from "../../../lib/moodtail-data.js";
import { FeedbackForm } from "./FeedbackForm.js";
import { SignInDialog } from "./SignInDialog.js";
import { rememberVibeBarIntent } from "../vibe-bar-intent.js";
import Draw from "../../draw/art.js";
import { genericMatchSharePath } from "../generic-match-share.js";

/* ── The brand's line-drawn guests. One is picked per drink (stable by
   serial) and drawn onto the drink, black ink on the paper card. ── */
const CARD_GUESTS = [
  { src: "/brand/ill-party.png", width: "42%", left: "50%", top: "1%", tx: "-46%", rotate: 0 },
  { src: "/brand/ill-legs.png", width: "28%", left: "54%", top: "16%", tx: "0%", rotate: 8 },
  { src: "/brand/ill-face.png", width: "36%", left: "8%", top: "22%", tx: "0%", rotate: -4 },
  { src: "/brand/ill-hand-open.png", width: "46%", left: "50%", top: "58%", tx: "-58%", rotate: -8 },
  { src: "/brand/ill-sitters.png", width: "36%", left: "32%", top: "12%", tx: "0%", rotate: 0 },
] as const;

function hash31(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0;
  return h;
}

function guestForSerial(serial: string) {
  return CARD_GUESTS[hash31(serial) % CARD_GUESTS.length]!;
}

/** Four-character catalogue number, stable for a given key. */
function makeSerial(key: string): string {
  const clean = key.replace(/\W/g, "");
  if (clean.length >= 8) return clean.slice(0, 4).toUpperCase();
  return hash31(key).toString(36).toUpperCase().padStart(4, "0").slice(-4);
}

/** The guest's own words for the back of the card. Only the mood is quotable —
 *  `freeText` carries the machine-built order summary (see buildPreference),
 *  not anything the guest actually said. Tag-only sessions fall back to tags. */
function originalVibeLine(preferences?: VenuePreferences): string | undefined {
  if (!preferences) return undefined;
  const spoken = preferences.mood?.trim();
  if (spoken) return spoken;
  const signals = [preferences.occasion, ...preferences.flavors].filter((s): s is string => Boolean(s?.trim()));
  return signals.length ? signals.join(", ") : undefined;
}

/* Both faces share one grid cell so the tallest face — not a fixed box —
 * sets the card's height; the 3/4 aspect is only the floor. Long model copy
 * grows the card instead of clipping the order line off the bottom. */
const FACE_STYLE = {
  gridArea: "1 / 1",
  background: "var(--paper-card)",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
} as const;

type MatchCardResult = VenueMatchResult | GenericCocktailMatch;

/** The result — a two-sided specimen card, tap to flip. The front is the
 *  poster: the drink with a house guest drawn onto it, the model's vibeName
 *  (the guest's night, not the menu's label), the roast, and the flavor
 *  chips. The back is the dossier: the guest's original vibe in their own
 *  words, tasting notes, why this pour, and what's actually in it. */
export function ResultCard({ destination, preferences, result, onAgain, onDestination }: {
  destination?: { label: string; url: string };
  preferences?: VenuePreferences;
  result: MatchCardResult;
  onAgain(): void;
  onDestination(): void;
}) {
  const { lang, t } = useLang();
  const [flipped, setFlipped] = useState(false);
  const [cardState, setCardState] = useState<"idle" | "working" | "done" | "error">("idle");
  const [shareState, setShareState] = useState<"idle" | "copied" | "shared">("idle");
  const [barState, setBarState] = useState<"idle" | "working" | "saved" | "error">("idle");
  const [signInFor, setSignInFor] = useState<"save" | "share" | null>(null);
  const reduceMotion = useReducedMotion();
  const generic = isGenericMatch(result);
  const matchId = generic ? undefined : result.matchId;
  const copy = matchCopy(result);
  const venueResult = generic ? undefined : result;
  const ingredientsSource = generic ? result.cocktail.ingredients : result.item.ingredients;
  const serial = makeSerial(matchId ?? result.traceId);
  const guest = guestForSerial(serial);
  const chips = copy.flavorProfile.split(/[,，、]/).map((s) => s.trim()).filter(Boolean).slice(0, 4);
  const originalVibe = originalVibeLine(preferences);
  const translatedIngredients = venueResult && venueResult.item.ingredientsZh?.length === venueResult.item.ingredients.length
    ? venueResult.item.ingredientsZh
    : undefined;
  const ingredients = lang === "zh" && translatedIngredients ? translatedIngredients : ingredientsSource;
  const baseSpiritIngredientIndex = venueResult?.item.ingredients.findIndex(
    (ingredient) => ingredient.toLowerCase() === venueResult?.item.baseSpirit?.toLowerCase(),
  );
  const translatedBaseSpirit = lang === "zh"
    ? translatedIngredients?.[baseSpiritIngredientIndex ?? -1]
    : undefined;

  // Tap anywhere to flip — unless the guest is selecting text to copy it.
  const flip = () => {
    if (window.getSelection()?.toString()) return;
    setFlipped((f) => !f);
  };

  async function saveCard() {
    setCardState("working");
    try {
      const { shareCardFile, deliverShareCard } = await import("../share-card.js");
      await deliverShareCard(await shareCardFile(result, originalVibe ?? "", lang));
      setCardState("done");
    } catch (caught) {
      setCardState((caught as Error).name === "AbortError" ? "idle" : "error");
    }
  }

  async function saveToVibeBar() {
    if (!matchId) return;
    setBarState("working");
    try {
      const { HttpVenueClient, VenueClientError } = await import("../../../clients/http-venue-client.js");
      try {
        await new HttpVenueClient().saveToVibeBar(matchId);
        setBarState("saved");
      } catch (caught) {
        if (caught instanceof VenueClientError && caught.status === 401) {
          // Not signed in: ask before redirecting — being thrown to Google
          // unannounced reads as a bug. The confirmed intent completes on
          // /vibe-bar after the round trip.
          setBarState("idle");
          setSignInFor("save");
          return;
        }
        throw caught;
      }
    } catch {
      setBarState("error");
    }
  }

  async function shareLink() {
    const { getAccessToken } = await import("../../auth/auth-session.js");
    if (!(await getAccessToken().catch(() => null))) {
      setSignInFor("share");
      return;
    }
    const path = matchId
      ? `/r/${matchId}`
      : generic && preferences
        ? genericMatchSharePath(result, preferences)
        : window.location.pathname;
    const url = new URL(path, window.location.origin).toString();
    const nav = navigator as Navigator & { share?: (data: { title: string; url: string }) => Promise<void> };
    const isTouch = window.matchMedia?.("(pointer: coarse)").matches
      || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    try {
      if (isTouch && nav.share) {
        await nav.share({ title: copy.vibeName, url });
        setShareState("shared");
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareState("copied");
    } catch (caught) {
      if ((caught as Error).name !== "AbortError") setShareState("idle");
    }
  }

  async function confirmGoogle() {
    if (!matchId && !(generic && preferences)) return;
    const { signInWithGoogle } = await import("../../auth/auth-session.js");
    if (signInFor === "save") {
      // Completed by /vibe-bar after the round trip.
      if (!matchId) return;
      rememberVibeBarIntent(matchId);
      await signInWithGoogle("/vibe-bar");
      return;
    }
    // Share: land on the very page being shared; its Copy link button is there.
    const path = matchId ? `/r/${matchId}` : genericMatchSharePath(result as GenericCocktailMatch, preferences!);
    await signInWithGoogle(path);
  }

  function resumeAfterEmailSignIn() {
    const intent = signInFor;
    setSignInFor(null);
    if (intent === "save") void saveToVibeBar();
    else if (intent === "share") void shareLink();
  }

  return <div className="poster-wrap" data-testid="match-result">
    {signInFor && <SignInDialog
      title={signInFor === "save" ? t("Sign in to keep this drink", "登录以保存这杯酒") : t("Sign in to share this match", "登录以分享此匹配")}
      description={signInFor === "save"
        ? t("Your Vibe Bar follows your account, so tonight’s match is still there next time.", "你的 Vibe Bar 会同步到你的账号，下次来它依然在。")
        : t("Sharing links your card to you, so the person on the other end sees whose night this was.", "分享会将卡片与你关联，让对方看到这是属于谁的夜晚。")}
      onGoogle={() => void confirmGoogle()}
      onSignedIn={resumeAfterEmailSignIn}
      onCancel={() => setSignInFor(null)}
    />}

    <div className="cursor-pointer" style={{ perspective: 1200 }} onClick={flip}>
      <motion.div
        className="relative grid w-full"
        style={{ aspectRatio: "3/4", transformStyle: "preserve-3d" }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.55, ease: [0.4, 0, 0.2, 1] }}
      >
        <CardFront chips={chips} guest={guest} hidden={flipped} result={result} onFlip={flip} />
        <CardBack hidden={!flipped} ingredients={ingredients} originalVibe={originalVibe} result={result} serial={serial} translatedBaseSpirit={translatedBaseSpirit} onFlip={flip} />
      </motion.div>
    </div>

    <div className="vt-actions poster-actions">
      {destination && <a className="btn btn-solid" href={destination.url} onClick={onDestination}>{destination.label}</a>}
      <button className={destination ? "btn btn-outline" : "btn btn-solid"} data-testid="save-card" disabled={cardState === "working"} type="button" onClick={() => void saveCard()}>
        {cardState === "working" ? t("Rendering card…", "生成卡片中…")
          : cardState === "done" ? t("Saved ✓", "已保存 ✓")
          : cardState === "error" ? t("Retry save card", "重试保存卡片")
          : t("Save card", "保存卡片")}
      </button>
      {matchId && <button className="btn btn-outline" data-testid="save-vibe-bar" disabled={barState === "working"} type="button" onClick={() => void saveToVibeBar()}>
        {barState === "working" ? t("Saving…", "保存中…")
          : barState === "saved" ? t("In your Vibe Bar ✓", "已存入 Vibe Bar ✓")
          : barState === "error" ? t("Retry Vibe Bar", "重试存入 Vibe Bar")
          : t("Save to Vibe Bar", "存入 Vibe Bar")}
      </button>}
      <button className="btn btn-outline" data-testid="share-link" type="button" onClick={() => void shareLink()}>
        {shareState === "copied" ? t("Link copied ✓", "链接已复制 ✓") : shareState === "shared" ? t("Shared ✓", "已分享 ✓") : t("Share", "分享")}
      </button>
      <button className="btn btn-outline" type="button" onClick={onAgain}>{t("Match again", "再测一次")}</button>
    </div>

    {matchId && <FeedbackForm key={matchId} matchId={matchId} />}
  </div>;
}

function CardFront({ chips, guest, hidden, result, onFlip }: {
  chips: string[];
  guest: (typeof CARD_GUESTS)[number];
  hidden: boolean;
  result: MatchCardResult;
  onFlip(): void;
}) {
  const { lang } = useLang();
  const zh = lang === "zh";
  const generic = isGenericMatch(result);
  const copy = matchCopy(result);
  const drinkName = generic ? result.cocktail.name : result.item.name;
  return <article aria-hidden={hidden} inert={hidden} className="paper-pocket pocket-card frame-gilt relative flex flex-col" style={FACE_STYLE}>
    <div className="grain-layer" aria-hidden style={{ opacity: 0.32 }} />

    {/* Masthead */}
    <div className="relative px-8 pt-7 text-center">
      <div className="mono-sm" style={{ letterSpacing: "0.3em" }}>{generic
        ? (zh ? "VIBETAIL · 今晚特调" : "VIBETAIL · ORIGINAL POUR")
        : `${result.venue.name.toUpperCase()} — ${result.menu.name.toUpperCase()}`}</div>
    </div>

    {/* The drink, with the house guest drawn onto it. The art is absolutely
        positioned so its intrinsic height never inflates the card — the well
        takes whatever height the text blocks leave over, and clips strays. */}
    <div className="relative min-h-0 w-full flex-1 overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center px-10 py-2">
        {generic
          ? <img src={result.illustrationUrl} alt={drinkName} className="drink-cutout max-h-full max-w-full" style={{ objectFit: "contain" }} />
          : <div className="relative flex h-full w-full items-center justify-center">
              {result.item.imageUrl
                ? <img src={result.item.imageUrl} alt={drinkName} className="drink-cutout max-h-full max-w-full" style={{ objectFit: "contain" }} />
                : <span className="block h-full" style={{ width: "48%", color: "var(--ink)" }}><Draw name="glass" strokeWidth={2} style={{ height: "100%" }} /></span>}
              <img src={guest.src} alt="" aria-hidden draggable={false} style={{ position: "absolute", width: guest.width, left: guest.left, top: guest.top, transform: `translateX(${guest.tx}) rotate(${guest.rotate}deg)`, filter: "invert(0.92)", pointerEvents: "none" }} />
            </div>}
      </div>
    </div>

    {/* Name, roast, flavors, order line */}
    <div className="relative px-8 pb-3 text-center">
      <h1 className="display mx-auto max-w-[22ch] text-[clamp(24px,4.5vw,34px)] leading-[1.06]" style={{ textTransform: "uppercase", letterSpacing: "0.03em" }}>{copy.vibeName}</h1>
      <p className="accent-italic mx-auto mt-3 max-w-[36ch] text-[19px] leading-snug" style={{ color: "var(--ink-soft)" }} data-testid="roast">“{copy.roast}”</p>
      {chips.length > 0 && <div className="mt-4 flex flex-wrap justify-center gap-1.5">
        {chips.map((flavor) => {
          const chipLabel = zh ? (FLAVOR_CHIPS.find((c) => c.label.toLowerCase() === flavor.toLowerCase())?.labelZh ?? flavor) : flavor;
          return <span key={flavor} className="mono-sm" style={{ border: "1px solid var(--line-strong)", padding: "5px 9px", letterSpacing: "0.18em" }}>{chipLabel}</span>;
        })}
      </div>}
      <OrderLine className="mt-4" result={result} testId="order-line" />
    </div>

    <TapHint onFlip={onFlip} />
  </article>;
}

function CardBack({ hidden, ingredients, originalVibe, result, serial, translatedBaseSpirit, onFlip }: {
  hidden: boolean;
  ingredients: string[];
  originalVibe?: string | undefined;
  result: MatchCardResult;
  serial: string;
  translatedBaseSpirit?: string | undefined;
  onFlip(): void;
}) {
  const { lang, t } = useLang();
  const zh = lang === "zh";
  const generic = isGenericMatch(result);
  const copy = matchCopy(result);
  const baseSpirit = generic ? result.cocktail.baseSpirit : translatedBaseSpirit ?? result.item.baseSpirit;
  const hasIngredients = generic
    ? result.cocktail.ingredients.length > 0 || Boolean(result.cocktail.baseSpirit)
    : result.item.ingredients.length > 0 || Boolean(result.item.baseSpirit);
  return <article aria-hidden={hidden} inert={hidden} className="paper-pocket pocket-card frame-gilt relative flex flex-col" style={{ ...FACE_STYLE, transform: "rotateY(180deg)" }}>
    <div className="grain-layer" aria-hidden style={{ opacity: 0.32 }} />

    {/* The dossier scrolls inside an absolutely-positioned well so its full
        text never inflates the card's grid row — the FRONT's content decides
        any growth past the 3/4 floor. Focusable so arrow/page keys scroll it;
        the thin scrollbar stays visible as the cue that it scrolls. */}
    <div className="relative min-h-0 flex-1">
    <div className="absolute inset-0 overflow-y-auto px-8 pb-2 pt-7" style={{ scrollbarWidth: "thin" }} tabIndex={0}>
      {/* Header */}
      <div className="pb-4" style={{ borderBottom: "1px solid var(--line-strong)" }}>
        <h2 className="display text-[clamp(21px,3.5vw,27px)] leading-[1.08]" style={{ textTransform: "uppercase", letterSpacing: "0.03em" }}>{copy.vibeName}</h2>
        <OrderLine result={result} />
      </div>

      {originalVibe && <div className="mt-5 p-4" style={{ border: "1px solid var(--line)", background: "var(--paper-warm)" }}>
        <BackLabel>{t("Original vibe", "你的 Vibe")}</BackLabel>
        <p className="accent-italic mt-2 text-[19px] leading-snug" style={{ color: "var(--ink-soft)" }} data-testid="original-vibe">“{originalVibe}”</p>
      </div>}

      <div className="mt-5">
        <BackLabel>{t("Tasting notes", "品鉴笔记")}</BackLabel>
        <p className="note mt-2 text-[14px] leading-relaxed">{copy.tastesLike}</p>
      </div>

      <div className="mt-5">
        <BackLabel>{t("Why this one", "为什么选它")}</BackLabel>
        <p className="note mt-2 text-[14px] leading-relaxed">{copy.whyThisMatch}</p>
      </div>

      {hasIngredients && <div className="mt-5">
        <div className="flex items-baseline justify-between gap-3">
          <BackLabel>{t("Ingredients", "配料")}</BackLabel>
          {baseSpirit && <span className="mono-sm">{zh ? `基酒 · ${baseSpirit}` : `Base · ${baseSpirit}`}</span>}
        </div>
        {ingredients.length > 0 && <ol className="mt-2">
          {ingredients.map((ingredient, i) => <li key={i} className="grid grid-cols-[30px_1fr] gap-3 py-1.5 text-[14px]" style={{ borderBottom: "1px solid var(--line-soft)" }}>
            <span className="specimen-no">{String(i + 1).padStart(2, "0")}</span>
            <span className="note">{ingredient}</span>
          </li>)}
        </ol>}
        <p className="note mt-2 text-[11.5px] italic" style={{ color: "var(--ink-mute)" }}>{generic
          ? t("A playful cocktail concept, made for your mood.", "一杯按心情创作的趣味特调。")
          : t("Final interpretation & execution reserved by the bar", "最终调制以酒吧实际出品为准")}</p>
      </div>}

      <div className="mt-6 flex items-end justify-between">
        <span className="specimen-no">No. {serial}</span>
        <span className="signature text-[22px]" style={{ color: "var(--ink-mute)" }}>Vibetail</span>
      </div>
    </div>
    </div>

    <TapHint onFlip={onFlip} />
  </article>;
}

function OrderLine({ className, result, testId }: { className?: string; result: MatchCardResult; testId?: string }) {
  const { lang } = useLang();
  const zh = lang === "zh";
  const drinkName = isGenericMatch(result) ? result.cocktail.name : result.item.name;
  const price = isGenericMatch(result) ? null : result.item.price;
  return <p className={className ? `mono-sm ${className}` : "mono-sm mt-2"} {...(testId ? { "data-testid": testId } : {})}>
    {zh ? "点单: " : "ORDER: "}{drinkName}{price ? ` · ${price}` : ""}
  </p>;
}

function isGenericMatch(result: MatchCardResult): result is GenericCocktailMatch {
  return "kind" in result && result.kind === "generic_cocktail";
}

function matchCopy(result: MatchCardResult) {
  return isGenericMatch(result)
    ? {
        vibeName: result.cocktail.name,
        tastesLike: result.cocktail.tastesLike,
        flavorProfile: result.cocktail.flavorProfile,
        whyThisMatch: result.cocktail.whyThisMatch,
        roast: result.cocktail.roast,
      }
    : result;
}

function BackLabel({ children }: { children: string }) {
  return <span className="mono-sm block" style={{ letterSpacing: "0.24em" }}>{children}</span>;
}

function TapHint({ onFlip }: { onFlip(): void }) {
  const { t } = useLang();
  return <div className="relative flex flex-none justify-center pb-4 pt-1">
    <button
      type="button"
      className="mono-sm flex cursor-pointer items-center gap-1.5"
      style={{ border: 0, background: "transparent", padding: 0 }}
      onClick={(event) => { event.stopPropagation(); onFlip(); }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t("Tap to flip", "点击翻面")}
    </button>
  </div>;
}
